import Plugin from "@ckeditor/ckeditor5-core/src/plugin";
import Widget from "@ckeditor/ckeditor5-widget/src/widget";

import {
  toWidget,
  viewToModelPositionOutsideModelElement,
} from "@ckeditor/ckeditor5-widget/src/utils";

import PlaceholderCommand from "./placeholdercommand";

import "./theme/placeholder.css";

export default class PlaceholderEditing extends Plugin {
  static get requires() {
    return [Widget];
  }

  init() {
    this._defineSchema();
    this._defineConverters();

    this.editor.commands.add(
      "placeholder",
      new PlaceholderCommand(this.editor),
    );

    this.editor.editing.mapper.on(
      "viewToModelPosition",
      viewToModelPositionOutsideModelElement(this.editor.model, (viewElement) =>
        viewElement.hasClass("placeholder"),
      ),
    );

    this.editor.config.define("placeholderProps", {
			showDisplayValues: false,
			types: {
				"client-first-name": {
					slug: "client-first-name",
					name: "Client's First Name",
					display: "Jeremy"
				},
				"client-last-name": {
					slug: "client-last-name",
					name: "Client's Last Name", 
					display: "Penrod"
				}
			}
    });

    this.editor.config.define("placeholderBrackets", {
      open: "{",
      close: "}"
    });
  }

  _defineSchema() {
    const schema = this.editor.model.schema;

    schema.register("placeholder", {
      // Allow wherever text is allowed:
      allowWhere: "$text",

      // The placeholder will acts as an inline node:
      isInline: true,

      // The inline-widget is self-contained so cannot be split by the caret and can be selected:
      isObject: true,

      // The placeholder can have many types, like date, name, surname, etc:
      allowAttributes: ["data-slug", "data-field-name", "data-display-val"], //"name", "data-slug", "data-field-name", 
    });
  }

  _defineConverters() {
    const conversion = this.editor.conversion;
    const config = this.editor.config;

    conversion.for("upcast").elementToElement({
      view: {
        name: "span",
        classes: ["placeholder"],
				attributes: [
					'data-slug',
					'data-field-name',
					'data-display-val'
				]
      },
      model: (viewElement, writer) => {
        const slug = viewElement.getAttribute('data-slug');
        const fieldName = viewElement.getAttribute('data-field-name');
        const displayVal = viewElement.getAttribute('data-display-val');
        const modelWriter = writer.writer || writer;
        return modelWriter.createElement("placeholder", { "data-slug": slug, "data-field-name": fieldName, "data-display-val": displayVal });
      },
    });

    conversion.for("editingDowncast").elementToElement({
      model: "placeholder",
      view: (modelItem, writer) => {
        const viewWriter = writer.writer || writer;

        const widgetElement = createPlaceholderView(modelItem, viewWriter);

        // Enable widget handling on placeholder element inside editing view.
        return toWidget(widgetElement, viewWriter);
      },
    });

    conversion.for("dataDowncast").elementToElement({
      model: "placeholder",
      view: (modelItem, writer) => {
        const viewWriter = writer.writer || writer;

        return createPlaceholderView(modelItem, viewWriter)
      },
    });

    // Helper method for both downcast converters.
    function createPlaceholderView(modelItem, viewWriter) {
      const slug = modelItem.getAttribute("data-slug");
      const fieldName = modelItem.getAttribute("data-field-name");
      const displayVal = modelItem.getAttribute("data-display-val");
			
			const showDisplayValues = config.get("placeholderProps.showDisplayValues");
			const placeholderPropTypes = config.get("placeholderProps.types");
			const textToDisplay = showDisplayValues ? placeholderPropTypes[slug].display : fieldName;

			const innerText = viewWriter.createText(
					(!textToDisplay || textToDisplay.trim() === "")
						? config.get("placeholderBrackets.open") + fieldName + config.get("placeholderBrackets.close")
						: textToDisplay
			);

      const placeholderView = viewWriter.createContainerElement("span", {
        class: "placeholder",
				"data-slug": slug,
				"data-field-name": fieldName,
				"data-display-val": displayVal,
      });

      viewWriter.insert(
        viewWriter.createPositionAt(placeholderView, 0),
        innerText,
      );

      return placeholderView;
    }
  }
}
